import moment from 'moment-jalaali';
import React, { useRef, useEffect } from 'react';
import { createUseStyles } from 'react-jss';
import { isIsoDate } from '../../utils/date';
import useNonInitialEffect from '../../hooks/useNonIntialEffect';

const styles = createUseStyles({
    time: {
        border: 'none',
        backgroundColor: 'white',
        color: '#363636',
        lineHeight: '1.5',
        width: '22px',
        fontSize: '1em',
        fontFamily: 'inherit',
        '&:focus': {
            outline: 'none'
        },
        '&::-webkit-input-placeholder': {
            /* Chrome/Opera/Safari */ fontSize: '1.2em'
        },
        '::-moz-placeholder': {
            /* Firefox 19+ */ fontSize: '1.2em'
        },
        ':-ms-input-placeholder': {
            /* IE 10+ */ fontSize: '1.2em'
        },
        ':-moz-placeholder': {
            /* Firefox 18- */ fontSize: '1.2em'
        }
    },
    splitter: {
        margin: '0 3px',
        width: '10px',
        height: '100%',
        position: 'relative',
        display: 'inline-block',
        overflow: 'visible',
        fontWeight: '800',
        fontSize: '1.4em',
        lineHeight: '1em',
        textAlign: 'center',
        pointerEvents: 'none'
    },
    timeContainer: ({ noAccess }) => ({
        padding: '.375rem .75rem',
        border: '1px solid #bbbbbb',
        borderRadius: '4px',
        lineHeight: '1.5',
        width: '40%',
        display: 'inline-flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: 'calc(1.5em + .75rem + 2px)',
        fontSize: '1em',
        fontFamily: 'inherit',
        direction: 'ltr',
        opacity: noAccess ? 0.5 : 1,
        '&:focus-within': {
            outline: 'none',
            borderColor: '#1b90fa',
            boxShadow: '0 0 0 0.2rem rgb(0 123 255 / 25%)'
        }
    })
});
interface TimeProps {
    name: string;
    value: string;
    setTime: Function;
    setDate: Function;
    setEditedHour: Function;
    setEditedMinute: Function;
    editedHour: string;
    editedMinute: string;
    required: boolean;
    handleAlgorithmCode: () => void;
    noAccess?: boolean;
    isChanging: string;
}
const Time: React.FC<TimeProps> = ({
    value,
    name,
    setTime,
    setDate,
    setEditedHour,
    setEditedMinute,
    editedHour,
    editedMinute,
    required,
    handleAlgorithmCode,
    noAccess,
    isChanging
}: TimeProps) => {
    const classes = styles({ noAccess });
    const timeRef = useRef(null);
    const timeWithLeadingZero = (time: string) => {
        return time?.length < 2 ? `0${time}` : time;
    };
    const date = value?.includes('T') ? value?.split('T')?.[0] : moment(value).format('YYYY-MM-DD');

    useEffect(() => {
        if (!new Date(value).toString().includes('Invalid Date') && value?.length > 10) {
            const hour = isIsoDate(value)
                ? value.split('T')[1].slice(0, 2)
                : moment(new Date(value)).format('HH');
            setEditedHour(hour);
            setEditedMinute(moment(new Date(value)).format('mm'));
        }
    }, [value]);
    useNonInitialEffect(() => {
        if (isChanging === 'changed' && isIsoDate(value)) {
            handleAlgorithmCode();
        }
    }, [value, isChanging]);
    const handleHourChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        let hour = event.target.value;
        let length = event.target.value.length;
        if (+hour > 23) {
            return;
        }
        if (!length) {
            setEditedHour('');
            setEditedMinute('');
            setTime('');
        }
        if (length > 2) {
            hour = hour.substring(0, 1);
            length = 1;
        }
        if (+hour > 2 || length > 1) {
            if (length === 1) {
                setEditedHour(`0${hour}`);
            } else {
                setEditedHour(hour);
            }
            if (timeRef && timeRef.current) {
                setEditedMinute('');
                timeRef.current.focus();
            }
        } else {
            setEditedHour(hour);
        }
    };
    const handleEmitValidValues = () => {
        let time = `${editedHour}:${timeWithLeadingZero(editedMinute)}`;
        if (!editedHour && !editedMinute) {
            return;
        }
        if (!editedHour) {
            return;
        }
        if (!editedMinute) {
            setEditedMinute('00');
            time = `${editedHour}:` + `00`;
        }
        if (time) {
            setTime(time);
            setDate(date);
        }
    };
    const handleMinuteChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        let minute = event.target.value;
        let length = event.target.value.length;
        if (length > 2) {
            minute = minute.substring(0, 1);
            length = 1;
        }
        if (length > 2 || +minute >= 60) {
            //
        } else if (length === 1 && [6, 7, 8, 9].includes(+minute)) {
            setEditedMinute(`0${minute.toString()}`);
        } else if (length > 1 && +minute === 60) {
            setEditedMinute('00');
        } else {
            setEditedMinute(minute.toString());
        }
    };
    return (
        <div className={classes.timeContainer}>
            <input
                type="text"
                className={classes.time}
                name={name}
                value={editedHour}
                onChange={handleHourChange}
                placeholder="- -"
                required={required}
                disabled={noAccess}
            />
            <div className={classes.splitter}>&#58;</div>
            <input
                ref={timeRef}
                type="text"
                className={classes.time}
                name={name}
                value={editedMinute}
                onBlur={() => handleEmitValidValues()}
                onChange={handleMinuteChange}
                placeholder="- -"
                disabled={noAccess}
            />
        </div>
    );
};
export default Time;
